import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LineChart as LineChartIcon, ArrowUpRight, ArrowDownRight, Minus, Loader2, Download, FileSpreadsheet, FileText, ChevronDown } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { linkedge, downloadAuditTimeline } from '../../lib/api';

/**
 * Compact, in-card SVG sparkline. No external chart deps so the bundle stays
 * small. Renders an area + line + dots for the given scores list.
 */
function Sparkline({ points, currentId, onPointClick }) {
  // points: [{ id, overall, label }]
  const N = points.length;
  if (N < 2) return null;
  const W = 720;
  const H = 96;
  const PAD_X = 20;
  const PAD_Y = 12;
  const xs = points.map((_, i) => PAD_X + (i * (W - 2 * PAD_X)) / (N - 1));
  const minVal = Math.min(...points.map((p) => p.overall ?? 0), 30);
  const maxVal = Math.max(...points.map((p) => p.overall ?? 0), 80);
  const range = Math.max(20, maxVal - minVal);
  const yFor = (v) => H - PAD_Y - ((Math.max(0, v - minVal)) / range) * (H - 2 * PAD_Y);
  const ys = points.map((p) => yFor(p.overall ?? 0));

  const linePath = xs.map((x, i) => `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${ys[i].toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L ${xs[N - 1].toFixed(1)} ${H - PAD_Y} L ${xs[0].toFixed(1)} ${H - PAD_Y} Z`;

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className="w-full h-24 select-none"
      role="img"
      aria-label="Score trajectory sparkline"
      data-testid="audit-timeline-sparkline"
    >
      {/* Baseline grid */}
      <line x1={PAD_X} y1={H - PAD_Y} x2={W - PAD_X} y2={H - PAD_Y} stroke="hsl(var(--border))" strokeWidth="1" />
      {/* Area fill */}
      <path d={areaPath} fill="hsl(var(--primary))" fillOpacity="0.08" />
      {/* Line */}
      <path d={linePath} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {/* Dots */}
      {points.map((p, i) => {
        const isCurrent = p.id === currentId;
        return (
          <g key={p.id || i}>
            <circle
              cx={xs[i]}
              cy={ys[i]}
              r={isCurrent ? 6 : 4}
              fill="hsl(var(--background))"
              stroke={isCurrent ? 'hsl(var(--accent))' : 'hsl(var(--primary))'}
              strokeWidth={isCurrent ? 2.5 : 1.75}
              style={{ cursor: onPointClick ? 'pointer' : 'default' }}
              onClick={onPointClick ? () => onPointClick(p) : undefined}
            />
            {/* Floating score label */}
            <text
              x={xs[i]}
              y={ys[i] - 10}
              textAnchor="middle"
              className="font-mono"
              fontSize="10"
              fill={isCurrent ? 'hsl(var(--accent))' : 'hsl(var(--foreground))'}
              opacity={isCurrent ? 1 : 0.7}
            >
              {p.overall}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function formatShort(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function relTime(iso) {
  if (!iso) return '';
  try {
    const ms = Date.now() - new Date(iso).getTime();
    const d = Math.round(ms / 86400000);
    if (d <= 0) return 'today';
    if (d === 1) return 'yesterday';
    if (d < 30) return `${d}d ago`;
    const m = Math.round(d / 30);
    return m < 12 ? `${m}mo ago` : `${Math.round(m / 12)}y ago`;
  } catch {
    return '';
  }
}

/**
 * Audit timeline card. Pulls all completed audits for the current profile_name
 * from /api/analyze/timeline and renders a sparkline + clickable list with
 * per-audit deltas.
 *
 * Hidden when only 0-1 audits exist (nothing meaningful to plot).
 */
export default function AuditTimeline({ profileName, currentId }) {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [exporting, setExporting] = useState(null); // 'csv' | 'pdf' | null
  const [expanded, setExpanded] = useState(false); // collapsed by default

  const onExport = async (format) => {
    if (exporting) return;
    setExporting(format);
    try {
      toast.info(`Preparing ${format.toUpperCase()} export...`);
      await downloadAuditTimeline(format, profileName || '');
      toast.success(`Audit timeline ${format.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e?.message || `Could not export ${format.toUpperCase()}`);
    } finally {
      setExporting(null);
    }
  };

  useEffect(() => {
    let cancel = false;
    if (!profileName) {
      setLoading(false);
      return () => {};
    }
    setLoading(true);
    linkedge
      .auditTimeline(profileName, 20)
      .then((data) => {
        if (cancel) return;
        // Only show items that have an overall score (incomplete audits filtered already by status=complete)
        const list = (data?.items || []).filter((d) => typeof d.overall === 'number');
        setItems(list);
        setLoading(false);
      })
      .catch((e) => {
        if (cancel) return;
        setErr(e?.message || 'Failed to load audit timeline');
        setLoading(false);
      });
    return () => {
      cancel = true;
    };
  }, [profileName]);

  const points = useMemo(() => items || [], [items]);

  if (loading) {
    return (
      <Card className="border-border" data-testid="audit-timeline-loading">
        <CardContent className="p-5 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={14} className="animate-spin" /> Loading audit timeline…
        </CardContent>
      </Card>
    );
  }
  if (err) {
    return null;
  }
  // Don't render if not enough history (single-audit profiles)
  if (!points.length || points.length < 2) return null;

  const first = points[0];
  const last = points[points.length - 1];
  const totalDelta = (last.overall ?? 0) - (first.overall ?? 0);
  const trend =
    totalDelta > 0 ? 'up' : totalDelta < 0 ? 'down' : 'flat';
  const trendBadge = (
    <Badge
      className={
        'font-mono-edge text-[10px] py-0.5 ' +
        (trend === 'up'
          ? 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
          : trend === 'down'
          ? 'bg-destructive/15 text-destructive border-destructive/30'
          : 'bg-secondary border-border text-muted-foreground')
      }
    >
      {trend === 'up' && <ArrowUpRight size={10} className="mr-1" />}
      {trend === 'down' && <ArrowDownRight size={10} className="mr-1" />}
      {trend === 'flat' && <Minus size={10} className="mr-1" />}
      {totalDelta > 0 ? `+${totalDelta}` : `${totalDelta}`} since first audit
    </Badge>
  );

  return (
    <Card className="border-border" data-testid="audit-timeline-card">
      <CardContent className="p-6">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          data-testid="audit-timeline-toggle"
          className="w-full flex items-center gap-2 flex-wrap text-left"
        >
          <LineChartIcon size={16} className="text-primary" />
          <h3 className="font-semibold">Audit history</h3>
          <Badge
            variant="secondary"
            className="font-mono-edge text-[10px]"
            data-testid="audit-timeline-count"
          >
            {points.length} audits
          </Badge>
          <span className="ml-auto flex items-center gap-2">
            {trendBadge}
            <ChevronDown
              size={16}
              className={
                'text-muted-foreground transition-transform duration-200 ' +
                (expanded ? 'rotate-180' : '')
              }
            />
          </span>
        </button>

        {expanded && (
          <div className="mt-4" data-testid="audit-timeline-expanded">
            <div className="flex justify-end mb-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 px-2.5 rounded-full text-[11px] font-medium gap-1"
                    data-testid="audit-timeline-export-btn"
                    disabled={!!exporting}
                    aria-label="Export audit timeline"
                  >
                    {exporting ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Download size={12} />
                    )}
                    Export
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="audit-timeline-export-menu">
                  <DropdownMenuItem
                    onClick={() => onExport('csv')}
                    data-testid="audit-timeline-export-csv"
                  >
                    <FileSpreadsheet size={14} className="mr-2 text-primary" />
                    Download as CSV
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => onExport('pdf')}
                    data-testid="audit-timeline-export-pdf"
                  >
                    <FileText size={14} className="mr-2 text-primary" />
                    Download as PDF
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            <Sparkline
              points={points}
              currentId={currentId}
              onPointClick={(p) => {
                if (p.id && p.id !== currentId) navigate(`/analysis/${p.id}`);
              }}
            />

            <div className="mt-4 divide-y divide-border" data-testid="audit-timeline-list">
          {points
            .slice()
            .reverse()
            .map((p, i, arr) => {
              const next = arr[i + 1];
              const delta = next ? (p.overall ?? 0) - (next.overall ?? 0) : null;
              const isCurrent = p.id === currentId;
              return (
                <button
                  type="button"
                  key={p.id}
                  onClick={() => p.id && !isCurrent && navigate(`/analysis/${p.id}`)}
                  disabled={isCurrent}
                  data-testid={`audit-timeline-row-${p.id}`}
                  className={
                    'w-full flex items-center gap-3 py-2.5 px-1 text-left rounded-sm transition-colors ' +
                    (isCurrent
                      ? 'cursor-default'
                      : 'hover:bg-secondary/60')
                  }
                >
                  <div className="flex flex-col items-center w-16 shrink-0">
                    <span
                      className={
                        'font-mono-edge text-base font-bold ' +
                        (isCurrent ? 'text-[hsl(var(--accent))]' : 'text-foreground')
                      }
                    >
                      {p.overall}
                    </span>
                    <span className="font-mono-edge text-[10px] text-muted-foreground">
                      / 100
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {formatShort(p.created_at)}
                      <span className="text-xs text-muted-foreground ml-2">
                        ({relTime(p.created_at)})
                      </span>
                      {isCurrent && (
                        <Badge
                          variant="outline"
                          className="ml-2 font-mono-edge text-[10px] border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))]"
                        >
                          CURRENT
                        </Badge>
                      )}
                    </p>
                    <p className="text-[11px] text-muted-foreground font-mono-edge mt-0.5">
                      H {p.categories?.headline ?? '—'} · A {p.categories?.about ?? '—'} · E{' '}
                      {p.categories?.experience ?? '—'} · S {p.categories?.skills ?? '—'}
                    </p>
                  </div>
                  {delta !== null && (
                    <Badge
                      className={
                        'font-mono-edge text-[10px] shrink-0 ' +
                        (delta > 0
                          ? 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30'
                          : delta < 0
                          ? 'bg-destructive/15 text-destructive border-destructive/30'
                          : 'bg-secondary border-border text-muted-foreground')
                      }
                    >
                      {delta > 0 ? `+${delta}` : delta === 0 ? '±0' : `${delta}`}
                    </Badge>
                  )}
                </button>
              );
            })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
